<script setup>
import { ref, onMounted, defineExpose } from 'vue';

const showBanner = ref(false);

// Prüfen, ob der Benutzer bereits zugestimmt hat
onMounted(() => {
  const cookiesAccepted = localStorage.getItem('cookiesAccepted');
  showBanner.value = !cookiesAccepted;
});

// Cookies akzeptieren
const acceptCookies = () => {
  localStorage.setItem('cookiesAccepted', 'true');
  localStorage.setItem('cookiePreference', 'all');
  showBanner.value = false;
};

// Nur notwendige Cookies akzeptieren
const acceptNecessaryCookies = () => {
  localStorage.setItem('cookiesAccepted', 'true');
  localStorage.setItem('cookiePreference', 'necessary');
  showBanner.value = false;
};

// Banner erneut anzeigen (für Widerruf/Änderung der Einstellungen)
const reopenBanner = () => {
  showBanner.value = true;
};

// Cookie-Einstellungen komplett zurücksetzen
const resetCookieSettings = () => {
  localStorage.removeItem('cookiesAccepted');
  localStorage.removeItem('cookiePreference');
  showBanner.value = true;
};

// Interner Link-Handler
const handleInternalLink = (e, path) => {
  // Nur behandeln, wenn es ein interner Link ist
  if (path.startsWith('/')) {
    e.preventDefault();
    // URL im Browser aktualisieren
    window.history.pushState(null, '', path);
    // Cookie-Banner schließen nach Navigation
    showBanner.value = false;
    // Zum Seitenanfang scrollen
    window.scrollTo(0, 0);
  }
};

// Methoden für externe Komponenten verfügbar machen
defineExpose({
  reopenBanner,
  resetCookieSettings
});
</script>

<template>
  <transition name="cookie-fade">
    <div v-if="showBanner" class="cookie-banner">
      <div class="cookie-content">
        <div class="cookie-text">
          <h3>Cookie-Hinweis</h3>
          <p>Wir verwenden Cookies und ähnliche Technologien, um Ihnen ein optimales Webseitenerlebnis zu bieten. Einige davon sind technisch notwendig, andere helfen uns, diese Website zu verbessern.</p>
          <p>Weitere Informationen finden Sie in unserer <a href="/datenschutz" @click="handleInternalLink($event, '/datenschutz')">Datenschutzerklärung</a>.</p>
        </div>
        <div class="cookie-actions">
          <button @click="acceptNecessaryCookies" class="btn-secondary">Nur notwendige Cookies</button>
          <button @click="acceptCookies" class="btn-primary">Alle akzeptieren</button>
        </div>
      </div>
    </div>
  </transition>
</template>

<style scoped>
.cookie-banner {
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  background-color: var(--white);
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  padding: 1rem;
}

.cookie-content {
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.cookie-text h3 {
  font-family: 'Playfair Display', serif;
  font-size: 1.25rem;
  color: var(--primary-color);
  margin-bottom: 0.5rem;
}

.cookie-text p {
  font-size: 0.95rem;
  margin-bottom: 0.5rem;
  color: var(--text-color);
}

.cookie-text a {
  color: var(--primary-color);
  text-decoration: none;
}

.cookie-text a:hover {
  text-decoration: underline;
}

.cookie-actions {
  display: flex;
  gap: 1rem;
}

.btn-primary, .btn-secondary {
  padding: 0.75rem 1.5rem;
  border-radius: 50px;
  font-weight: 500;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.3s ease;
  border: none;
}

.btn-primary {
  background-color: var(--primary-color);
  color: white;
}

.btn-secondary {
  background-color: #f5f5f5;
  color: var(--text-color);
  border: 1px solid #e0e0e0;
}

.btn-primary:hover {
  background-color: var(--primary-dark);
}

.btn-secondary:hover {
  background-color: #e8e8e8;
}

.cookie-fade-enter-active,
.cookie-fade-leave-active {
  transition: opacity 0.5s, transform 0.5s;
}

.cookie-fade-enter-from,
.cookie-fade-leave-to {
  opacity: 0;
  transform: translateY(30px);
}

@media (min-width: 768px) {
  .cookie-content {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
  
  .cookie-text {
    flex: 1;
  }
  
  .cookie-actions {
    flex-shrink: 0;
  }
}
</style> 